import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import TruncatedText from "@/components/common/TruncatedText";
import { formatDateTime, formatRelativeTime } from "@/utils/dateUtils";
import { MessageDelivery } from "@/types/message-delivery";
import { DeliveryStatusBadge } from "./DeliveryStatusBadge";
import { DeliveryDetailPopover } from "./DeliveryDetailPopover";

const COLS = 8;

interface DeliveriesTableProps {
  rows: MessageDelivery[];
  isLoading: boolean;
  isError: boolean;
}

export const DeliveriesTable: React.FC<DeliveriesTableProps> = ({
  rows,
  isLoading,
  isError,
}) => {
  const { t } = useTranslation();

  return (
    <div className="border-base-border overflow-hidden rounded-xl border bg-white shadow-sm">
      <Table>
        <TableHeader>
          <TableRow className="bg-secondary-badge hover:bg-secondary-badge h-10">
            <TableHead className="pl-4">
              {t("communicationChannels.deliveries.columns.time")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.direction")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.channel")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.status")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.recipient")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.body")}
            </TableHead>
            <TableHead>
              {t("communicationChannels.deliveries.columns.booking")}
            </TableHead>
            <TableHead className="pr-4">
              {t("communicationChannels.deliveries.columns.details")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell
                colSpan={COLS}
                className="py-12 text-center text-sm text-black/45"
              >
                {t("communicationChannels.deliveries.page.loading")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading && isError && (
            <TableRow>
              <TableCell
                colSpan={COLS}
                className="text-custom-destructive-dark py-12 text-center text-sm"
              >
                {t("communicationChannels.deliveries.page.loadError")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={COLS}
                className="py-12 text-center text-sm text-black/45"
              >
                {t("communicationChannels.deliveries.empty")}
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            !isError &&
            rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="pl-4 whitespace-nowrap">
                  <div className="flex flex-col">
                    <span className="text-sm text-black/96">
                      {formatDateTime(row.queuedAt)}
                    </span>
                    <span className="text-xs text-black/45">
                      {formatRelativeTime(row.queuedAt)}
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1 text-sm text-black/96">
                    {row.direction === "OUTBOUND" ? (
                      <ArrowUpRight className="size-4" aria-hidden />
                    ) : (
                      <ArrowDownLeft className="size-4" aria-hidden />
                    )}
                    {t(
                      `communicationChannels.deliveries.direction.${row.direction}`,
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-black/96">
                  {t(`communicationChannels.composer.badge.${row.channel}`)}
                </TableCell>
                <TableCell>
                  <DeliveryStatusBadge status={row.status} />
                </TableCell>
                <TableCell className="max-w-[16rem]">
                  {row.recipientAddress ? (
                    <TruncatedText
                      limit={32}
                      className="text-sm break-all text-black/96"
                    >
                      {row.recipientAddress}
                    </TruncatedText>
                  ) : (
                    <span className="text-black/45">—</span>
                  )}
                </TableCell>
                <TableCell className="max-w-sm">
                  {row.bodyPreview ? (
                    <TruncatedText
                      limit={80}
                      className="text-sm break-words text-black/96"
                    >
                      {row.bodyPreview}
                    </TruncatedText>
                  ) : (
                    <span className="text-black/45">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {row.bookingId ? (
                    <Link
                      to={`/reservations/${row.bookingId}`}
                      className="text-royal-glow-3 text-sm hover:underline"
                    >
                      {t("communicationChannels.deliveries.viewBooking")}
                    </Link>
                  ) : (
                    <span className="text-black/45">—</span>
                  )}
                </TableCell>
                <TableCell className="pr-4">
                  <DeliveryDetailPopover row={row} />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
};
